import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Lock, RefreshCw, Unlock, X } from 'lucide-react';
import MainService from '../../services/MainService';
import ClientListGrid, { useClientListGrid } from '../ui/ClientListGrid';

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const AREA_COLUMNS = [
  { field: 'name', title: 'Name', hideable: true },
  { field: 'status', title: 'Status', hideable: true },
  { field: 'actions', title: 'Options', hideable: true, sortable: false, filterable: false, groupable: false, align: 'right' },
];

const getCellValue = (row, field) => {
  switch (field) {
    case 'name':
      return row.Name || '';
    case 'status':
      return row.IsLockRef ? 'Numbers Will Not Skip' : 'Numbers Will Skip';
    default:
      return row[field] ?? '';
  }
};

const AreaStoreNoLock = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const columns = useMemo(() => AREA_COLUMNS, []);
  const grid = useClientListGrid({ columns, rows: areas, getCellValue });

  const loadAreas = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.AreasInDropDown();
      setAreas(parseList(response));
    } catch (err) {
      console.error('Load area locks error:', err);
      setAreas([]);
      setError('Failed to load areas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const lockArea = async (area) => {
    if (!area?.Id) return;

    try {
      setTogglingId(area.Id);
      setError('');
      const response = await MainService.LockAreaRef(area.Id);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadAreas();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Toggle area ref lock error:', err);
      setError('Network Error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Lock className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Area Store No Lock</h1>
              <p className="mt-1 text-sm text-gray-500">Control whether store reference numbers skip already used numbers by area.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadAreas}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ClientListGrid
        columns={columns}
        grid={grid}
        loading={loading}
        loadingText="Loading areas..."
        emptyText="No areas found."
        minWidth="640px"
        rowKey={(row) => row.Id}
        renderCell={(row, column) => {
          if (column.field === 'name') {
            return <span className="font-semibold text-gray-900">{row.Name || '-'}</span>;
          }
          if (column.field === 'status') {
            return (
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${row.IsLockRef ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'}`}>
                {row.IsLockRef ? 'Numbers Will Not Skip' : 'Numbers Will Skip'}
              </span>
            );
          }
          if (column.field === 'actions') {
            return (
              <button
                type="button"
                onClick={() => lockArea(row)}
                disabled={togglingId === row.Id}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {row.IsLockRef ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {row.IsLockRef ? 'Unlock' : 'Lock'}
              </button>
            );
          }
          return getCellValue(row, column.field);
        }}
      />
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default AreaStoreNoLock;
